import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'

import { IDataProviderFilter } from 'core-components/types'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { CrudStore } from 'core/types/crudStore'
import { IRequestSpecialization } from 'onboarding/components/widgets/public/admin/specializations/models'
import {
  IInvalidParamsErrorResponsePayload,
  ILinkedSpecializationErrorResponsePayload,
  IResponseErrorPayload,
} from 'onboarding/types/response'
import {
  throwOnErrorWithoutPayload,
  throwOnInvalidRequestParams,
  throwOnLinkedSpecializationError,
} from 'onboarding/utils/errorResponse/throwOnApiError'

const CONTROLLER = 'specialization'

export class SpecializationApi {
  public getListProgramElementsCrud(crudKey?: string) {
    return CrudStore.get(CrudStoreItemOnboarding.SpecializationProgramElements, crudKey)
  }

  public clearCacheListProgramElementsCrud(crudKey?: string, needTriggerSubscribers?: boolean) {
    this.getListProgramElementsCrud(crudKey).removeQueries(needTriggerSubscribers)
  }

  public async listProgramElements(filter: IDataProviderFilter) {
    const response = await WebSocketStore.get('onboarding').call<{
      items: { programElement: string; programElementOrder: number | null }[]
    }>({
      service: CONTROLLER,
      action: 'listProgramElements',
      payload: { kendoConditions: filter },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.items
  }

  public async create(payload: TSaveSpecialization) {
    const response = await WebSocketStore.get('onboarding').call<{
      item: IRequestSpecialization
      errorPayload?: IInvalidParamsErrorResponsePayload | IResponseErrorPayload
    }>({
      service: CONTROLLER,
      action: 'create',
      payload: { payload },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to create specialization'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.item
  }

  public async update(id: number, payload: TSaveSpecialization) {
    const response = await WebSocketStore.get('onboarding').call<{
      item: IRequestSpecialization
      errorPayload?:
        | IInvalidParamsErrorResponsePayload
        | ILinkedSpecializationErrorResponsePayload
        | IResponseErrorPayload
    }>({
      service: CONTROLLER,
      action: 'update',
      payload: { id, payload },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to update specialization'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnLinkedSpecializationError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.item
  }

  public async deleteOne(id: number): Promise<boolean> {
    const response = await WebSocketStore.get('onboarding').call<{
      deleted: boolean
      errorPayload?:
        | IInvalidParamsErrorResponsePayload
        | ILinkedSpecializationErrorResponsePayload
        | IResponseErrorPayload
    }>({
      service: CONTROLLER,
      action: 'deleteOne',
      payload: { id },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to delete specialization'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnLinkedSpecializationError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.deleted
  }
}

export const specializationApi = new SpecializationApi()

export type TSaveSpecialization = Omit<
  IRequestSpecialization,
  'Modified' | 'ModifiedBy' | 'Created' | 'CreatedBy' | 'EntryId'
>
